import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSiteContext } from "./contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { saveChatHistory, loadChatHistory, startNewChat } from "./services/auditStorageService";
import { saveArtifact, type ArtifactType } from "./services/artifactService";
import { useMCPContext, type PrioritizedIssue } from "./hooks/useMCPContext";
import QuickFixButton from "./quick-fixes/QuickFixButton";
import PostFixActions from "./quick-fixes/PostFixActions";
import { getCmsImplementationGuide, type IssueFixType } from "./quick-fixes/cmsGuides";
import { resolveFixType, resolveArtifactFile, downloadArtifact } from "./quick-fixes/fixArtifacts";
import { buildRubyKnowledgeContext } from "./knowledge/rubyKnowledge";
import SiteSwitcher from "./dashboard/SiteSwitcher";
import ReportSwitcher from "./dashboard/ReportSwitcher";
import { canGenerateFix } from "@/components/subscriptions/subscriptionService";
import { type ReportRecommendations } from "./services/mcpService";
import {
  Send,
  Sparkles,
  Paperclip,
  Loader2,
  User,
  RotateCcw,
  History,
  Copy,
  Check,
  Save,
  Download,
  Code,
  ChevronRight,
  Zap,
  Wrench,
} from "lucide-react";

// ── Master chat identity (own chat-history key so it doesn't collide with per-card chats) ──
const MASTER_CARD_ID = "ruby-master";
const MASTER_TAB_ID = "master";

const BRAND_GRADIENT = "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)";

// Card accent + remediation priority order (AI Risk → Search → Brand → AI Readiness → Local).
const CARD_META: Record<string, { name: string; color: string; order: number }> = {
  "ai-vulnerability": { name: "AI Risk & Exposure", color: "#EF4444", order: 0 },
  "search-intelligence": { name: "Search Intelligence", color: "#8B5CF6", order: 1 },
  "brand-insight": { name: "Brand Intelligence", color: "#FF7A18", order: 2 },
  "ai-readiness": { name: "AI Readiness", color: "#3B82F6", order: 3 },
  "local-intelligence": { name: "Local Intelligence", color: "#22C55E", order: 4 },
};

const cardColor = (cardId: string) => CARD_META[cardId]?.color ?? "#8B5CF6";
const cardName = (cardId: string, fallback?: string) => CARD_META[cardId]?.name ?? fallback ?? cardId;

interface CodeBlock {
  code: string;
  language: string;
  title: string;
  artifactType: string;
  filename?: string;
  mime?: string;
  emphasize?: boolean;
  accent?: string;
}

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  content?: string;
  quickFixList?: boolean;
  quickFixButton?: boolean;
  issues?: PrioritizedIssue[];
  codeBlock?: CodeBlock;
  postFixActions?: boolean;
  activeIssueId?: string;
  activeIssueCard?: string;
  retryAction?: boolean;
  cmsOptions?: boolean;
  cmsGuide?: boolean;
  isWelcome?: boolean;
}

const CMS_OPTIONS = ["WordPress", "Shopify", "Wix", "Squarespace", "Webflow", "Custom HTML", "Other"];

function renderContent(content: string) {
  return content.split("\n").map((line, i) => (
    <React.Fragment key={i}>
      {line.includes("**") ? (
        <div className={i > 0 ? "mt-1" : ""}>
          {line.split("**").map((part, j) =>
            j % 2 === 1 ? (
              <span key={j} className="font-semibold" style={{ color: "#E8E4FF" }}>
                {part}
              </span>
            ) : (
              <span key={j}>{part}</span>
            )
          )}
        </div>
      ) : (
        <div className={i > 0 && line ? "mt-1" : ""}>{line}</div>
      )}
    </React.Fragment>
  ));
}

/** Splits a Ruby response into prose + the first fenced code block. */
function parseRubyResponse(raw: string, fallbackType: IssueFixType): { analysisText: string; code: string; language: string } {
  let language = fallbackType === "schema" || fallbackType === "faq" ? "json" : "html";
  let code = "";
  let analysisText = raw || "";
  const codeMatch = analysisText.match(/```(\w+)?\n([\s\S]*?)```/);
  if (codeMatch) {
    language = codeMatch[1] || language;
    code = codeMatch[2].trim();
    analysisText = analysisText.replace(/```[\s\S]*?```/g, "").trim();
  }
  return { analysisText, code, language };
}

const RubyMasterChat: React.FC = () => {
  const { activeSite, selectedReportId } = useSiteContext();
  const { user } = useAuth();
  const domain = activeSite?.domain || "";

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [workingSeconds, setWorkingSeconds] = useState(0);
  useEffect(() => {
    if (!isLoading) { setWorkingSeconds(0); return; }
    setWorkingSeconds(0);
    const started = Date.now();
    const tick = window.setInterval(() => setWorkingSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(tick);
  }, [isLoading]);
  const [hasHistory, setHasHistory] = useState(false);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);
  const [activeIssue, setActiveIssue] = useState<PrioritizedIssue | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatSessionRef = useRef<string>("");

  const {
    isLoading: mcpLoading,
    error: mcpError,
    overview,
    priorityQueue,
    allIssues,
    markIssueFixed,
    getIssueById,
    generateFixPrompt,
    buildContextForClaude,
  } = useMCPContext({ domain, reportId: selectedReportId });

  const historyReportId = selectedReportId || overview?.reportId;
  const historyTabId = JSON.stringify([MASTER_TAB_ID, domain, historyReportId]);
  const historyContextRef = useRef(historyTabId);
  historyContextRef.current = historyTabId;

  // Cross-card pending issues, in the MCP's prioritised order.
  const masterIssues = useMemo(
    () =>
      (priorityQueue?.priorityQueue ?? []).filter(
        (i) => i.status === "pending" && !completedIds.includes(i.id)
      ),
    [priorityQueue, completedIds]
  );

  const scrollToBottom = () => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const initWelcome = useCallback(() => {
    setActiveIssue(null);
    setCompletedIds([]);
    setIsLoading(false);
    setMessages([{ id: `welcome-${Date.now()}`, role: "assistant", isWelcome: true }]);
  }, []);

  useEffect(() => {
    const sessionKey = historyTabId;
    if (!domain) {
      chatSessionRef.current = "";
      initWelcome();
      return;
    }
    if (mcpLoading) return;
    if (chatSessionRef.current === sessionKey) return;
    chatSessionRef.current = sessionKey;
    initWelcome();
  }, [domain, historyTabId, mcpLoading, initWelcome]);

  const openQuickFixes = () => {
    setMessages((prev) => [
      ...prev,
      { id: `user-qf-${Date.now()}`, role: "user", content: "⚡ Quick Fixes — start fixing" },
      { id: `qf-list-${Date.now()}`, role: "assistant", quickFixList: true, issues: masterIssues },
    ]);
    scrollToBottom();
  };

  const generateFix = async (issue: PrioritizedIssue, extraContext?: string, options?: { isRetry?: boolean }) => {
    const requestContext = historyTabId;
    const isCurrentContext = () => historyContextRef.current === requestContext;
    setActiveIssue(issue);
    if (!options?.isRetry) {
      setMessages((prev) => [
        ...prev,
        { id: `user-fix-${Date.now()}`, role: "user", content: `Generate fix: ${issue.title}` },
      ]);
    }
    setIsLoading(true);
    scrollToBottom();

    const fixType = resolveFixType(issue);

    try {
      if (!options?.isRetry) {
        const allowed = await canGenerateFix();
        if (!isCurrentContext()) return;
        if (!allowed) {
          setMessages((prev) => [
            ...prev,
            {
              id: `no-units-${Date.now()}`,
              role: "assistant",
              content:
                "You're out of remediation units for this cycle. Top up with a booster pack on **Subscriptions**, or wait until your monthly allowance renews.",
              activeIssueId: issue.id,
              activeIssueCard: issue.card,
            },
          ]);
          return;
        }
      }

      const contextParts: string[] = [];
      if (domain) contextParts.push(`**Authority context:**\n${await buildContextForClaude()}`);
      if (extraContext) contextParts.push(`**User clarification:** ${extraContext}`);

      const fixPrompt = await generateFixPrompt({ ...issue, fixType }, domain, contextParts.join("\n\n"));
      if (!isCurrentContext()) return;

      const { data, error } = await supabase.functions.invoke("ruby-ai-seo-analysis", {
        body: {
          websiteUrl: domain ? `https://${domain}/` : "",
          userId: user?.id,
          issueId: issue.id,
          userMessage: fixPrompt,
          region: "au",
          analysisType: issue.analysisType,
        },
      });

      if (!isCurrentContext()) return;
      if (error) throw error;
      if (data?.success === false) throw new Error(data?.error || "Fix generation failed");

      let analysisText = data?.analysis || "";
      let code = "";
      let language = fixType === "schema" || fixType === "faq" ? "json" : "html";

      if (analysisText.includes("synthesis failed") || analysisText.includes("analysis failed")) {
        if (fixType === "entity" && issue.steps?.length) {
          analysisText = issue.steps.map((s) => `**Step ${s.step}** (${s.tag}): ${s.instruction}`).join("\n\n");
        } else {
          throw new Error("Ruby couldn't produce a fix for this issue");
        }
      } else {
        const parsed = parseRubyResponse(analysisText, fixType);
        analysisText = parsed.analysisText;
        code = parsed.code;
        language = parsed.language;
        if (!code) {
          code = data?.analysis || "";
          language =
            fixType === "schema" || fixType === "faq"
              ? "json"
              : fixType === "meta" || fixType === "title" || fixType === "h1"
                ? "html"
                : "text";
          analysisText = "";
        }
      }

      if (!code && !analysisText) throw new Error("Ruby returned an empty response");

      const file = code ? resolveArtifactFile(fixType, language, issue.title) : undefined;
      setMessages((prev) => [
        ...prev,
        {
          id: `code-${Date.now()}`,
          role: "assistant",
          content: (fixType === "faq" || (fixType === "schema" && /faq/i.test(issue.title)))
            ? "**DRAFT — NOT VERIFIED FOR PUBLICATION**\n\nRuby has generated a possible FAQ schema example, not an implementation-ready fix. Before using it, confirm each specific target URL, ensure every question and answer is visible and accurate on that page, and obtain editorial approval. Do not assume FAQ markup creates Google rich results or guarantees AI citations. This issue remains open until implementation is verified.\n\n" + (analysisText || "Review this proposed draft against your published content.")
            : (analysisText || `Here's your **${issue.title}** — ready for review:`),
          codeBlock: code
            ? {
                code,
                language,
                title: issue.title,
                artifactType: fixType,
                filename: file?.filename,
                mime: file?.mime,
                emphasize: file?.emphasize,
                accent: cardColor(issue.card),
              }
            : undefined,
          postFixActions: !(fixType === "faq" || (fixType === "schema" && /faq/i.test(issue.title))),
          activeIssueId: issue.id,
          activeIssueCard: issue.card,
        },
      ]);
    } catch (err) {
      if (!isCurrentContext()) return;
      const reason = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        {
          id: `gen-error-${Date.now()}`,
          role: "assistant",
          content: `⚠️ **Couldn't generate your fix**\n\n${reason}\n\nTap **Try again** and I'll retry.`,
          retryAction: true,
          activeIssueId: issue.id,
          activeIssueCard: issue.card,
        },
      ]);
    } finally {
      if (isCurrentContext()) { setIsLoading(false); scrollToBottom(); }
    }
  };

  const handleRetryGeneration = (issueId: string) => {
    const issue = getIssueById(issueId) ?? activeIssue;
    if (issue) generateFix(issue, undefined, { isRetry: true });
  };

  const handleIssueClick = async (issue: PrioritizedIssue) => {
    if (issue.needsClarification) {
      setMessages((prev) => [
        ...prev,
        { id: `user-${Date.now()}`, role: "user", content: issue.title },
        {
          id: `clarify-${Date.now()}`,
          role: "assistant",
          content: `Before I generate this fix, what's the main topic or angle you'd like to focus on for **${issue.title}**?`,
        },
      ]);
      setActiveIssue(issue);
      scrollToBottom();
      return;
    }
    await generateFix(issue);
  };

  const handleMarkedFixed = async (issueId: string, issueCard?: string) => {
    const issue = getIssueById(issueId) ?? activeIssue;
    const card = issueCard || issue?.card;
    if (domain && card) await markIssueFixed(issueId, card);
    setCompletedIds((prev) => (prev.includes(issueId) ? prev : [...prev, issueId]));
    const remaining = masterIssues.filter((i) => i.id !== issueId);
    setMessages((prev) => [
      ...prev,
      {
        id: `continue-${Date.now()}`,
        role: "assistant",
        content:
          remaining.length > 0
            ? `✅ Fix marked complete! Continue with your remaining Quick Fixes (${remaining.length} left):`
            : "🎉 All issues fixed across your Smart Cards! Great work.",
        quickFixList: remaining.length > 0,
        issues: remaining,
      },
    ]);
    scrollToBottom();
  };

  const handleNeedHelp = () => {
    setMessages((prev) => [
      ...prev,
      {
        id: `cms-${Date.now()}`,
        role: "assistant",
        content: "I'd be happy to help you implement this! What CMS is your website built on?",
        cmsOptions: true,
      },
    ]);
    scrollToBottom();
  };

  const handleCmsSelect = (cms: string) => {
    const issue = activeIssue;
    const fixType: IssueFixType = issue ? resolveFixType(issue) : "technical";
    const guide = getCmsImplementationGuide(cms, fixType, issue?.title ?? "this issue");
    const planSteps = issue?.steps?.length
      ? `\n\n**From your action plan:**\n${issue.steps.map((s) => `**Step ${s.step}:** ${s.instruction}`).join("\n\n")}`
      : "";
    setMessages((prev) => [
      ...prev,
      { id: `user-cms-${Date.now()}`, role: "user", content: cms },
      {
        id: `cms-guide-${Date.now()}`,
        role: "assistant",
        content: `${guide}${planSteps}`,
        postFixActions: true,
        cmsGuide: true,
        activeIssueId: issue?.id,
        activeIssueCard: issue?.card,
      },
    ]);
    scrollToBottom();
  };

  const handleNewChat = () => {
    if (!domain || !historyReportId || mcpLoading) return;
    if (messages.length > 1) {
      saveChatHistory({ userId: user?.id, cardId: MASTER_CARD_ID, tabId: historyTabId, domain, messages }).catch(() => {});
    }
    startNewChat({ userId: user?.id, cardId: MASTER_CARD_ID, tabId: historyTabId }).catch(() => {});
    chatSessionRef.current = "";
    initWelcome();
    setHasHistory(true);
  };

  const handleLoadHistory = () => {
    if (!domain || !historyReportId || mcpLoading) return;
    loadChatHistory({ userId: user?.id, cardId: MASTER_CARD_ID, tabId: historyTabId }).then((loaded) => {
      if (historyContextRef.current !== historyTabId) return;
      if (loaded?.length) {
        setMessages(loaded as ChatMessage[]);
        setHasHistory(false);
      }
    });
  };

  // Free-form conversational path — grounded in the full authority context + Ruby knowledge.
  const askRuby = async (text: string) => {
    const requestContext = historyTabId;
    const isCurrentContext = () => historyContextRef.current === requestContext;
    setIsLoading(true);
    try {
      const knowledge = buildRubyKnowledgeContext();
      const ctx = domain ? await buildContextForClaude() : "No site connected.";
      if (!isCurrentContext()) return;
      const userMessage = `${knowledge}\n\n=== AUTHORITY CONTEXT (this user's live data across all 5 Smart Cards) ===\n${ctx}\n\n=== USER MESSAGE ===\n${text}\n\nRespond as Ruby: be direct and specific to this user's data. If a concrete fix artifact is appropriate, include it in a single fenced code block.`;

      const { data, error } = await supabase.functions.invoke("ruby-ai-seo-analysis", {
        body: {
          websiteUrl: domain ? `https://${domain}/` : "",
          userId: user?.id,
          userMessage,
          region: "au",
          analysisType: "technical",
        },
      });

      if (!isCurrentContext()) return;
      if (error) throw error;
      if (data?.success === false) throw new Error(data?.error || "Ruby couldn't respond");

      const raw = data?.analysis || "";
      if (!raw) throw new Error("Empty response");
      const { analysisText, code, language } = parseRubyResponse(raw, "technical");
      const file = code ? resolveArtifactFile("technical", language, "ruby-fix") : undefined;

      setMessages((prev) => [
        ...prev,
        {
          id: `ruby-${Date.now()}`,
          role: "assistant",
          content: analysisText || (code ? "Here's what I've got for you:" : raw),
          codeBlock: code
            ? {
                code,
                language,
                title: "Ruby fix",
                artifactType: "technical",
                filename: file?.filename,
                mime: file?.mime,
                emphasize: file?.emphasize,
                accent: "#8B5CF6",
              }
            : undefined,
        },
      ]);
    } catch {
      if (!isCurrentContext()) return;
      setMessages((prev) => [
        ...prev,
        {
          id: `ruby-fallback-${Date.now()}`,
          role: "assistant",
          content:
            "I can help with that. Use **Quick Fixes** to work through your prioritised issues across all five cards, or tell me a specific issue to fix.",
          quickFixButton: masterIssues.length > 0,
        },
      ]);
    } finally {
      if (isCurrentContext()) { setIsLoading(false); scrollToBottom(); }
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading) return;
    const text = inputValue.trim();
    setInputValue("");
    setMessages((prev) => [...prev, { id: `user-${Date.now()}`, role: "user", content: text }]);
    scrollToBottom();

    if (activeIssue?.needsClarification) {
      generateFix(activeIssue, text);
      setActiveIssue(null);
      return;
    }
    askRuby(text);
  };

  // ── Grouped (by card, in priority order) quick-fix list ──
  const renderGroupedList = (issues: PrioritizedIssue[]) => {
    const pending = issues.filter((i) => i.status !== "fixed" && !completedIds.includes(i.id));
    if (pending.length === 0) {
      return (
        <div className="ml-[34px] mt-3 rounded-xl px-4 py-6 text-center text-sm text-og01-text-muted">
          🎉 All issues fixed! Great work improving your site.
        </div>
      );
    }
    const groups = new Map<string, PrioritizedIssue[]>();
    for (const i of pending) {
      if (!groups.has(i.card)) groups.set(i.card, []);
      groups.get(i.card)!.push(i);
    }
    const ordered = Array.from(groups.entries()).sort(
      (a, b) => (CARD_META[a[0]]?.order ?? 9) - (CARD_META[b[0]]?.order ?? 9)
    );

    return (
      <div className="ml-[34px] mt-3 space-y-4 rounded-xl border border-white/[0.07] bg-[#0F0F18] p-4">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4" style={{ color: "#8B5CF6" }} />
          <span className="text-sm font-semibold text-og01-text">Quick Fixes across your Smart Cards</span>
        </div>
        {ordered.map(([cid, list]) => (
          <div key={cid}>
            <div className="mb-2 flex items-center gap-2">
              <span className="h-2 w-2 flex-none rounded-full" style={{ background: cardColor(cid) }} />
              <span className="text-xs font-semibold" style={{ color: cardColor(cid) }}>
                {cardName(cid, list[0]?.cardName)}
              </span>
              <span className="text-[11px] text-og01-text-muted">
                {list.length} to fix
              </span>
            </div>
            <div className="space-y-2">
              {list.map((issue) => (
                <QuickFixButton key={issue.id} issue={issue} onClick={() => handleIssueClick(issue)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const total = overview?.totalIssues;
  const remainingIssues = useMemo(() => {
    const source =
      allIssues && allIssues.length > 0
        ? allIssues
        : (priorityQueue?.priorityQueue ?? []);
    return source.filter(
      (issue) => issue.status !== "fixed" && !completedIds.includes(issue.id)
    );
  }, [allIssues, priorityQueue, completedIds]);
  const hasIssueList = remainingIssues.length > 0 || (allIssues?.length ?? 0) > 0;
  const pendingCount = hasIssueList ? remainingIssues.length : (total?.pending ?? masterIssues.length);
  const pendingTotals = useMemo(() => {
    let high = 0, medium = 0, low = 0;
    for (const issue of remainingIssues) {
      if (issue.priority === "HIGH") high++;
      else if (issue.priority === "MEDIUM") medium++;
      else low++;
    }
    return {
      high: hasIssueList ? high : (total?.high ?? 0),
      medium: hasIssueList ? medium : (total?.medium ?? 0),
      low: hasIssueList ? low : (total?.low ?? 0),
      total: pendingCount,
      fixed: total?.fixed ?? 0,
      pending: pendingCount,
    };
  }, [remainingIssues, hasIssueList, pendingCount, total]);
  const topIssue = masterIssues[0];

  return (
    <div className="animate-fade-in flex min-h-[calc(100vh-96px)] flex-col px-6 pt-6 md:px-8">
      <div className="mx-auto flex w-full max-w-[980px] flex-1 flex-col">
        {/* Header */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div
            className="grid h-[40px] w-[40px] flex-none place-items-center rounded-[12px]"
            style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 22px -8px rgba(139,92,246,.7)" }}
          >
            <Sparkles className="h-[22px] w-[22px] text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-[17px] font-semibold leading-tight text-og01-text">Ruby AIssist</h1>
            <p className="text-[12.5px] text-og01-text-muted">Master remediation across all five Smart Cards</p>
          </div>
          <div className="flex-1" />
          <SiteSwitcher />
          <ReportSwitcher />
        </div>

        {/* Chat card */}
        <div className="flex min-h-[560px] flex-1 flex-col overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card">
          {/* Messages */}
          <div className="flex-1 space-y-3.5 overflow-y-auto px-4 py-5 md:px-6">
            {messages.map((msg) => (
              <div key={msg.id}>
                {(msg.content || msg.quickFixButton || msg.isWelcome) && (
                  <div className={`flex w-full items-start gap-2.5 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                    <div
                      className="mt-0.5 grid h-[28px] w-[28px] flex-none place-items-center rounded-lg"
                      style={{ background: msg.role === "assistant" ? BRAND_GRADIENT : "linear-gradient(135deg,#2a2440,#3a2a52)" }}
                    >
                      {msg.role === "assistant" ? (
                        <Sparkles className="h-[14px] w-[14px] text-white" />
                      ) : (
                        <User className="h-[14px] w-[14px] text-white" />
                      )}
                    </div>
                    <div
                      className={`px-[15px] py-3 text-[13.5px] leading-relaxed text-og01-text-secondary ${
                        msg.role === "user" ? "ml-auto max-w-[85%]" : msg.isWelcome ? "min-w-0 flex-1" : "max-w-[85%]"
                      }`}
                      style={{
                        background: "#0F0F18",
                        border: "1px solid rgba(255,255,255,.07)",
                        borderRadius: msg.role === "user" ? "12px 4px 12px 12px" : "4px 12px 12px 12px",
                      }}
                    >
                      {msg.isWelcome ? (
                        <MasterWelcome
                          domain={domain}
                          mcpLoading={mcpLoading}
                          mcpError={mcpError}
                          score={overview?.compositeScore.score ?? 0}
                          band={overview?.compositeScore.band ?? ""}
                          totals={pendingTotals}
                          pendingCount={pendingCount}
                          topIssue={overview?.recommendations?.status === "structured" ? topIssue : undefined}
                          recommendations={overview?.recommendations}
                          reportNotice={overview?.reportNotice}
                        />
                      ) : (
                        msg.content && renderContent(msg.content)
                      )}
                    </div>
                  </div>
                )}

                {masterIssues.length > 0 && (msg.quickFixButton || msg.isWelcome) && (
                  <div className="ml-[38px] mt-2.5 flex justify-start">
                    <button
                      type="button"
                      onClick={openQuickFixes}
                      className="flex w-full max-w-[420px] items-center justify-between gap-2.5 rounded-xl px-4 py-3 text-sm font-semibold text-white transition-all hover:brightness-110"
                      style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 24px -12px rgba(255,122,24,.6)" }}
                    >
                      <span className="flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        Quick Fixes — start fixing
                      </span>
                      <ChevronRight className="h-4 w-4 flex-none opacity-90" />
                    </button>
                  </div>
                )}

                {msg.quickFixList && msg.issues && renderGroupedList(msg.issues)}

                {msg.cmsOptions && (
                  <div className="ml-[38px] mt-2 flex flex-wrap gap-2">
                    {CMS_OPTIONS.map((cms) => (
                      <button
                        key={cms}
                        type="button"
                        onClick={() => handleCmsSelect(cms)}
                        className="rounded-lg border border-white/[0.07] bg-[#0F0F18] px-3 py-1.5 text-xs font-medium text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text"
                      >
                        {cms}
                      </button>
                    ))}
                  </div>
                )}

                {msg.codeBlock && (
                  <div
                    className="ml-[38px] mt-3 overflow-hidden rounded-xl bg-[#0F0F18]"
                    style={{ border: `1px solid color-mix(in srgb, ${msg.codeBlock.accent || "#8B5CF6"} 26%, transparent)` }}
                  >
                    <div
                      className="flex items-center justify-between px-4 py-2.5"
                      style={{ background: `color-mix(in srgb, ${msg.codeBlock.accent || "#8B5CF6"} 10%, transparent)` }}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <Code className="h-[15px] w-[15px] shrink-0" style={{ color: msg.codeBlock.accent || "#8B5CF6" }} />
                        <span className="truncate text-xs font-semibold text-og01-text">{msg.codeBlock.title}</span>
                        {msg.codeBlock.filename && (
                          <span
                            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] text-og01-text-secondary"
                            style={{ fontFamily: "'Geist Mono', ui-monospace, monospace", background: "rgba(255,255,255,0.06)" }}
                          >
                            {msg.codeBlock.filename}
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(msg.codeBlock!.code);
                            setCopiedCodeId(msg.id);
                            setTimeout(() => setCopiedCodeId(null), 2000);
                          }}
                          className="flex items-center gap-1 rounded px-2 py-1 text-[10px] text-og01-text-secondary"
                        >
                          {copiedCodeId === msg.id ? <Check className="h-3 w-3" style={{ color: "#22C55E" }} /> : <Copy className="h-3 w-3" />}
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            downloadArtifact(
                              msg.codeBlock!.code,
                              msg.codeBlock!.filename || `${msg.codeBlock!.artifactType}.txt`,
                              msg.codeBlock!.mime || "text/plain"
                            )
                          }
                          className="flex items-center gap-1 rounded px-2 py-1 text-[10px]"
                          style={
                            msg.codeBlock.emphasize
                              ? { color: "#fff", background: msg.codeBlock.accent || "#8B5CF6" }
                              : { color: "var(--og01-text-secondary, #9AA0AA)" }
                          }
                          title={`Download ${msg.codeBlock.filename || "file"}`}
                        >
                          <Download className="h-3 w-3" /> Download
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            saveArtifact(
                              {
                                domain,
                                cardId: msg.activeIssueCard || MASTER_CARD_ID,
                                type: msg.codeBlock!.artifactType as ArtifactType,
                                title: msg.codeBlock!.title,
                                description: `Generated for ${domain}`,
                                code: msg.codeBlock!.code,
                                language: msg.codeBlock!.language,
                                targetUrl: `https://${domain}/`,
                              },
                              user?.id
                            )
                          }
                          className="flex items-center gap-1 rounded px-2 py-1 text-[10px]"
                          style={{ color: "#FF7A18" }}
                        >
                          <Save className="h-3 w-3" /> Save
                        </button>
                      </div>
                    </div>
                    <pre
                      className="overflow-x-auto px-4 py-3 text-xs text-og01-text-secondary"
                      style={{ fontFamily: "'Geist Mono', ui-monospace, monospace", maxHeight: "320px" }}
                    >
                      <code>{msg.codeBlock.code}</code>
                    </pre>
                  </div>
                )}

                {msg.retryAction && msg.activeIssueId && (
                  <div className="ml-[38px] mt-2 flex justify-start">
                    <button
                      type="button"
                      onClick={() => handleRetryGeneration(msg.activeIssueId!)}
                      disabled={isLoading}
                      className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold text-white transition-all hover:brightness-110 disabled:opacity-50"
                      style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 24px -12px rgba(255,122,24,.6)" }}
                    >
                      <RotateCcw className="h-4 w-4" />
                      Try again
                    </button>
                  </div>
                )}

                {msg.postFixActions && msg.activeIssueId && (
                  <PostFixActions
                    cmsGuide={msg.cmsGuide}
                    onFixed={() => handleMarkedFixed(msg.activeIssueId!, msg.activeIssueCard)}
                    onNeedHelp={handleNeedHelp}
                  />
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex items-start gap-2.5">
                <div className="grid h-[28px] w-[28px] place-items-center rounded-lg" style={{ background: BRAND_GRADIENT }}>
                  <Sparkles className="h-[14px] w-[14px] text-white" />
                </div>
                <div
                  className="flex items-center gap-2 px-[15px] py-3 text-[13.5px] text-og01-text-secondary"
                  style={{ background: "#0F0F18", border: "1px solid rgba(255,255,255,.07)", borderRadius: "4px 12px 12px 12px" }}
                >
                  <Loader2 className="h-4 w-4 animate-spin" style={{ color: "#8B5CF6" }} />
                  <div className="space-y-1">
                    <p className="font-medium">Ruby is preparing your response…</p>
                    <p className="text-xs text-og01-text-muted">Reviewing your report and preparing recommendations for your approval. Complex fixes may take 1–3 minutes.</p>
                    <p className="text-xs text-og01-text-muted" role="status">
                      {Math.floor(workingSeconds / 60)}:{String(workingSeconds % 60).padStart(2, "0")} elapsed
                      {workingSeconds >= 60 ? " · Still working — please keep this page open." : ""}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Mini actions */}
          <div className="flex flex-shrink-0 gap-2 border-t border-white/[0.07] px-4 py-2.5">
            <button
              type="button"
              onClick={handleLoadHistory}
              disabled={!hasHistory}
              className={`inline-flex items-center gap-1.5 rounded-lg border border-white/[0.07] bg-[#0F0F18] px-[11px] py-1.5 text-xs font-semibold text-og01-text-secondary transition-colors hover:border-white/[0.12] ${hasHistory ? "" : "opacity-40"}`}
            >
              <History className="h-[13px] w-[13px]" />
              Load Chat History
            </button>
            <button
              type="button"
              onClick={handleNewChat}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-white/[0.07] bg-[#0F0F18] px-[11px] py-1.5 text-xs font-semibold text-og01-text-secondary transition-colors hover:border-white/[0.12]"
            >
              <RotateCcw className="h-[13px] w-[13px]" />
              New Chat
            </button>
          </div>

          {/* Composer */}
          <div className="flex-shrink-0 border-t border-white/[0.07] px-4 py-3">
            <div
              className="flex items-center gap-2.5 rounded-xl bg-[#0F0F18] px-3 py-2.5"
              style={{ border: "1px solid rgba(255,255,255,.07)" }}
            >
              <input
                type="text"
                placeholder="Ask Ruby anything, or fix an issue across any of your five cards…"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
                className="flex-1 bg-transparent text-[13.5px] text-og01-text outline-none placeholder:text-og01-text-muted"
              />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="grid place-items-center text-og01-text-muted hover:text-og01-text-secondary">
                <Paperclip className="h-[17px] w-[17px]" />
              </button>
              <button type="button" onClick={handleSendMessage} className="grid place-items-center" style={{ color: "#8B5CF6" }}>
                <Send className="h-[17px] w-[17px]" />
              </button>
            </div>
            <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.txt" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Master welcome (cross-card) ──
const MasterWelcome: React.FC<{
  domain: string;
  mcpLoading: boolean;
  mcpError: string | null;
  score: number;
  band: string;
  totals?: { high: number; medium: number; low: number; total: number; fixed: number; pending: number };
  pendingCount: number;
  topIssue?: PrioritizedIssue;
  recommendations?: ReportRecommendations;
  reportNotice?: string | null;
}> = ({ domain, mcpLoading, mcpError, score, band, totals, pendingCount, topIssue, recommendations, reportNotice }) => {
  if (!domain) {
    return <p>👋 I&apos;m Ruby. Add a site using the switcher above and I&apos;ll pull your audit across all five Smart Cards so we can start fixing.</p>;
  }
  if (mcpLoading) {
    return <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" style={{ color: "#8B7BF7" }} /> Loading your authority profile…</p>;
  }
  if (mcpError) {
    return <p>⚠️ I couldn&apos;t load your data ({mcpError}). Try again shortly.</p>;
  }

  const notice = reportNotice ? (
    <p className="text-xs leading-relaxed" style={{ color: "#FBBF24" }}>{reportNotice}</p>
  ) : null;

  if (recommendations?.status === "saved_priority") {
    return (
      <div className="space-y-2.5">
        {notice}
        <p>
          👋 I&apos;m Ruby, your remediation engine for <strong>{domain}</strong>
          {score > 0 ? <> — composite score <strong>{score}</strong>{band ? ` (${band})` : ""}.</> : "."}
        </p>
        <p>This report has a saved priority, not a ranked action list.</p>
        <p style={{ color: "#E8E4FF" }}>{recommendations.narrative}</p>
        <p className="flex items-center gap-1.5 text-xs" style={{ color: "#9AA0B3" }}>
          Your chat history is saved — pick up anytime.
        </p>
      </div>
    );
  }

  if (recommendations?.status === "unavailable") {
    return (
      <div className="space-y-2.5">
        {notice}
        <p>
          👋 I&apos;m Ruby, your remediation engine for <strong>{domain}</strong>
          {score > 0 ? <> — composite score <strong>{score}</strong>{band ? ` (${band})` : ""}.</> : "."}
        </p>
        <p>Recommendations unavailable for this report.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {notice}
      <p>
        👋 I&apos;m Ruby, your remediation engine. Across your five Smart Cards for <strong>{domain}</strong> I found{" "}
        <strong>{pendingCount}</strong> open {pendingCount === 1 ? "issue" : "issues"}
        {score > 0 ? <> — composite score <strong>{score}</strong>{band ? ` (${band})` : ""}.</> : "."}
      </p>

      {totals && pendingCount > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {totals.high > 0 && (
            <span className="rounded-md border border-red-500/20 bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-400">{totals.high} High</span>
          )}
          {totals.medium > 0 && (
            <span className="rounded-md border border-amber-500/20 bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-400">{totals.medium} Med</span>
          )}
          {totals.low > 0 && (
            <span className="rounded-md border border-green-500/20 bg-green-500/15 px-2 py-0.5 text-[11px] font-semibold text-green-400">{totals.low} Low</span>
          )}
        </div>
      )}

      {topIssue ? (
        <p>
          Best place to start: <strong>{topIssue.title}</strong> on <strong>{cardName(topIssue.card, topIssue.cardName)}</strong> ({topIssue.priority} priority).
          Hit <strong>Quick Fixes</strong> and I&apos;ll work through everything in priority order — you can also just ask me anything.
        </p>
      ) : (
        <p style={{ color: "#4ADE80" }}>🎉 Everything looks clean across your Smart Cards. Ask me anything, or re-run your audit to refresh.</p>
      )}

      <p className="flex items-center gap-1.5 text-xs" style={{ color: "#9AA0B3" }}>
        Your chat history is saved — pick up anytime.
      </p>
    </div>
  );
};

export default RubyMasterChat;

